import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import React, { Fragment, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AuthCredentials } from "../../../dataTypes/Auth";
import { ThemeConfig } from "../../../dataTypes/ThemeConfig";
import FormErrorSuccessMsg from "../../shared/formErrorSuccessMsg/FormErrorSuccessMsg";

interface ForgotPasswordProps {
  themeConfig: ThemeConfig;
}

export const ForgotPassword: React.FC<ForgotPasswordProps> = ({
  themeConfig,
}) => {
  const { slug } = useParams<{ slug: string }>();
  const [payload, setPayload] = useState<AuthCredentials>({ email: "" });
  const [showMessage, setShowMessage] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [success, setSuccess] = useState<boolean>(false);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setPayload((prev) => ({ ...prev, [name]: value }));
    setShowMessage(false); // Reset message when user starts typing
  };

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    setShowMessage(false);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/${slug}/auth/password/email?v2=true`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      setShowMessage(true);
      setSuccess(response.ok);

      if (response.ok) {
        setPayload({ email: "" });
      }
    } catch (error) {
      console.error("Error", error);
      setShowMessage(true);
      setSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Fragment>
      <Box className="auth">
        <Box width="100%">
          <Box display="flex" alignItems="center" justifyContent="center">
            <img src={themeConfig.logo_img} alt="Logo" />
          </Box>
          {!success && (
            <Box className="centered-text" sx={{ marginBottom: "16px" }}>
              <Typography variant="body1">
                <strong style={{ display: "block", textAlign: "center" }}>
                  Enviaremos um e-mail com instruções sobre como redefinir sua
                  senha.
                </strong>
              </Typography>
            </Box>
          )}
          {showMessage && (
            <Box>
              {success ? (
                <FormErrorSuccessMsg
                  message={
                    "E-mail enviado com sucesso!\nVerifique sua caixa de entrada."
                  }
                  success={true}
                />
              ) : (
                <FormErrorSuccessMsg
                  message={"Email inválido"}
                  success={false}
                />
              )}
            </Box>
          )}
          {!success && (
            <form onSubmit={onSubmit}>
              <Box className="form-group">
                <label htmlFor="email" style={{ fontWeight: 700 }}>
                  Email
                </label>
                <TextField
                  id="outlined-basic-email"
                  name="email"
                  value={payload?.email}
                  variant="outlined"
                  onChange={handleInputChange}
                  type="email"
                  fullWidth
                  sx={{
                    "& .MuiInputBase-root": {
                      height: "40px",
                      fontSize: "0.875rem",
                      borderRadius: "8px",
                    },
                    "& .MuiOutlinedInput-input": {
                      padding: "10px 14px",
                      borderRadius: "8px",
                    },
                  }}
                />
              </Box>
              <Button
                type="submit"
                variant="contained"
                sx={{
                  bgcolor: themeConfig.color,
                  marginTop: "16px",
                  fontWeight: 700,
                  textTransform: "capitalize",
                  fontSize: "18px",
                  borderRadius: "8px",
                }}
                fullWidth
                disabled={loading}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Enviar"
                )}
              </Button>
            </form>
          )}
          <Button
            variant="outlined"
            sx={{
              marginTop: "16px",
              color: themeConfig.color,
              borderRadius: "8px",
              textTransform: "capitalize",
              fontSize: "18px",
              fontWeight: 700,
            }}
            fullWidth
            onClick={() => navigate(`/${slug}/login`)}
          >
            Voltar para login
          </Button>
        </Box>
      </Box>
    </Fragment>
  );
};

export default ForgotPassword;
